import React, { useMemo, useCallback, useContext, useEffect } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import { Stack } from '@bingads-webui-react/fabric-control';
import { GeneratedUI } from '@bingads-webui-campaign-react/ui-generator';
import { summaryPageComponentTypes } from '@bingads-webui-campaign-react/summary-page';
import { WIZARD_EVENTS, useWizardService, getCurrentFlow } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { SummaryDiagnostic } from '@bingads-webui-campaign-react/summary-diagnostic';
import { buildSchema } from './schema';
import { useSummarySchemaData } from './use-summary-schema-data';
import { STEP_PATHS } from '../step-constants';
import { SummaryShimmer } from '../../shared/components/summary-shimmer';
import { extractAuditPoints } from './diagnostics/utils';
import { useActionCallback } from './diagnostics/use-action-callback';

export const SummaryStepShell = ({
  data,
  onDataChange,
}) => {
  const { issueList, recommendList } = useMemo(() => extractAuditPoints(_.get(data, 'diagnosticsData')), [data]);
  const onActionClick = useActionCallback();
  const isLoading = useMemo(() => _.isEmpty(data), [data]);

  const i18n = useContext(I18nContext);

  const schemaData = useSummarySchemaData({ data });

  const { send, state } = useWizardService();

  const currentFlow = getCurrentFlow(state);

  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const {
    PMaxCreationDiagnostic: isPMaxCreationDiagnosticEnabled,
  } = permissions.dynamic;

  const onAssetsHeaderActionClick = useCallback(() => {
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.ASSETS,
    });
  }, [send]);

  const onTargetingHeaderActionClick = useCallback(() => send(WIZARD_EVENTS.NAVIGATE_TO, {
    stepId: STEP_PATHS.TARGETING,
  }), [send]);

  const onCampaignSettingsHeaderActionClick = useCallback(() =>
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.CAMPAIGNDETAILS,
    }), [send]);

  const onBudgetSettingsHeaderActionClick = useCallback(() =>
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.BUDGET,
    }), [send]);

  const onProductsHeaderActionClick = useCallback(() =>
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.PRODUCTS,
    }), [send]);

  const options = useMemo(() => ({
    onAssetsHeaderActionClick,
    onTargetingHeaderActionClick,
    onCampaignSettingsHeaderActionClick,
    onBudgetSettingsHeaderActionClick,
    onProductsHeaderActionClick,
  }), [onAssetsHeaderActionClick, onTargetingHeaderActionClick, onCampaignSettingsHeaderActionClick, onBudgetSettingsHeaderActionClick, onProductsHeaderActionClick]);

  const schema = useMemo(() => buildSchema(options), [options]);

  useEffect(() => (
    onDataChange({
      data,
      isValid: true,
    })
  ), [data, onDataChange]);

  return (
    <div className="summary-step" >
      <Stack tokens={{ childrenGap: 22 }}>
        <Stack>
          <div className="page-header-2">
            <h1> {i18n.getString(_TL_('Prepare for success!'))} </h1>
            <div className="page-description">
              <span>{i18n.getString(_TL_('Here\'s a summary of everything in your campaign. All objects are available, but campaign is not live yet.'))}</span>
            </div>
          </div>
        </Stack>
        {isLoading ? <SummaryShimmer currentSteps={currentFlow} /> : (
          <>
            {isPMaxCreationDiagnosticEnabled && <SummaryDiagnostic issues={issueList} recommendations={recommendList} onActionClick={onActionClick} />}
            <div className="summary-sections">
              <GeneratedUI schema={schema} customComponentTypes={summaryPageComponentTypes} {...schemaData} />
            </div>
          </>
        )
        }
      </Stack>
    </div>
  );
};

SummaryStepShell.propTypes = {
  data: PropTypes.shape({}),
  onDataChange: PropTypes.func.isRequired,
};

SummaryStepShell.defaultProps = {
  data: null,
};
